import { API_ROUTES, FEATURES, normalizeDeviceCapabilities } from '@dgm/shared';
import { api } from './api.js';

/**
 * Device-capability capture (CA Stage-1, CA-003/CA-005;
 * FEATURES.deviceTelemetry). Probes WebGPU availability (+ coarse adapter
 * vendor/architecture — no renderer strings, no fingerprinting machinery),
 * `navigator.deviceMemory`, and `navigator.hardwareConcurrency`, then POSTs
 * the normalized payload to /me/device-telemetry.
 *
 * Invariants:
 * - CONSENT-GATED: never probes without `preferences.analyticsOptIn` (the
 *   server independently re-checks, CA-003). Flag off → zero code paths run.
 * - Once per SPA session: module-level guard; called from the auth store at
 *   session establishment (hydrate + login, CA-005).
 * - Never throws, never blocks: fire-and-forget async, every error swallowed;
 *   absent APIs become nulls (nulls are data — "API absent" is a tier signal).
 *
 * HOW TO DISABLE / REMOVE: FEATURES.deviceTelemetry=false; full removal
 * checklist in packages/shared/src/schemas/device-telemetry.ts.
 */

/** Minimal structural WebGPU surface — a local interface instead of the
 *  `@webgpu/types` dep (no new packages for two optional fields). */
interface GpuAdapterLike {
  info?: { vendor?: unknown; architecture?: unknown };
  /** Older Chromium versions expose the async variant instead of `.info`. */
  requestAdapterInfo?: () => Promise<{ vendor?: unknown; architecture?: unknown } | undefined>;
}
interface NavigatorExtras {
  gpu?: { requestAdapter?: () => Promise<GpuAdapterLike | null> };
  deviceMemory?: unknown;
}

let attempted = false;

/** Test-only escape hatch for the module-level once-guard. */
export function resetDeviceTelemetryGuardForTests(): void {
  attempted = false;
}

/**
 * Capture + send once per session, iff the feature is on AND the user has
 * opted in to analytics. Safe to call repeatedly and from anywhere.
 */
export function maybeCaptureDeviceTelemetry(analyticsOptIn: boolean | null | undefined): void {
  if (!FEATURES.deviceTelemetry || analyticsOptIn !== true || attempted) return;
  attempted = true;
  void probeAndSend().catch(() => undefined);
}

async function probeAndSend(): Promise<void> {
  const nav = navigator as Navigator & NavigatorExtras;
  const webgpuApi = 'gpu' in nav;
  let adapterOk: boolean | null = null;
  let gpuVendor: unknown;
  let gpuArchitecture: unknown;

  if (webgpuApi) {
    try {
      const adapter = await nav.gpu?.requestAdapter?.();
      adapterOk = !!adapter;
      if (adapter) {
        const info =
          adapter.info ?? (await adapter.requestAdapterInfo?.().catch(() => undefined));
        gpuVendor = info?.vendor;
        gpuArchitecture = info?.architecture;
      }
    } catch {
      adapterOk = false; // API present, adapter unobtainable — itself a signal
    }
  }

  const payload = normalizeDeviceCapabilities({
    webgpuApi,
    adapterOk,
    gpuVendor,
    gpuArchitecture,
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
  });
  await api.post(API_ROUTES.deviceTelemetry.path, payload);
}
